import { TableSortLabel, Paper, Alert, IconButton, Stack } from "@mui/material";
import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  checkIfMetricsExist,
  getSelectedTimePeriod,
  getValueForDashBoard,
  roundStringNumberToInt,
} from "../../helpers/businessleaderDashboardHelpers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import {
  additionalQueriesrows01,
  additionalQueriestableHeadings01,
} from "./AdditionalQueriesConstants";
import DownloadIcon from "@mui/icons-material/Download";
import { saveAs } from "file-saver";

interface Props {
  lastMonthTargetVsActualData: any;
}

const TargetVSActualPreviousMonth = ({
  lastMonthTargetVsActualData,
}: Props) => {
  const headCells = Object.entries(additionalQueriestableHeadings01);

  const [order, setOrder] = useState("desc") as any;
  const [orderBy, setOrderBy] = useState("Metrics") as any;

  const dates = getSelectedTimePeriod("Last Month");

  const parseValue = (value: any) => {
    if (typeof value === "string") {
      if (value.includes("%")) {
        return parseFloat(value.replace("%", ""));
      }
      if (value.includes("$")) {
        return parseFloat(value.replace("$", ""));
      } else {
        return value.toLowerCase();
      }
    } else if (value === null || !value) {
      return "";
    }
    return value;
  };

  const handleRequestSort = (property: any) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = [...additionalQueriesrows01].sort((a, b) => {
    let valuea = "";
    let valueb = "";
    let dataa = getValueForDashBoard(
      a["metric"],
      lastMonthTargetVsActualData
    ) as any;
    if (dataa) {
      valuea = dataa[orderBy];
    }
    let datab = getValueForDashBoard(
      b["metric"],
      lastMonthTargetVsActualData
    ) as any;
    if (datab) {
      valueb = datab[orderBy];
    }
    const aValue = parseValue(valuea);
    const bValue = parseValue(valueb);

    if (aValue < bValue) {
      return order === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });

  const getMetricValue = (metricName: string, fieldName: string) => {
    const data = getValueForDashBoard(
      metricName,
      lastMonthTargetVsActualData
    ) as any;
    let value = data[fieldName];
    if (value !== null && value !== 0) {
      if (data["Metrics"] === "CVR" && fieldName === "Metrics") {
        return "CVR%";
      } else if (fieldName === "Attainment" || data["Metrics"] === "CVR") {
        return roundStringNumberToInt(value * 100, true) + "%";
      } else if (fieldName !== "Metrics" && metricName !== "ftd") {
        return roundStringNumberToInt(value) + "$";
      } else {
        return value;
      }
    } else if (value === null) {
      return "null";
    } else {
      return value;
    }
  };

  const getMetricValueForCSV = (metricName: string, fieldName: string) => {
    const data = getValueForDashBoard(
      metricName,
      lastMonthTargetVsActualData
    ) as any;
    let value = null as any;
    if (data) {
      value = data[fieldName];
    }
    if (value !== null && value !== 0) {
      if (data["Metrics"] === "CVR" && fieldName === "Metrics") {
        return "CVR%";
      } else if (fieldName === "Attainment" || data["Metrics"] === "CVR") {
        value = value * 100;
        value = Math.round(value.toFixed(2));
        return value + "%";
      } else if (fieldName !== "Metrics" && metricName !== "ftd") {
        value = Math.round(value);
        return value + "$";
      } else {
        return value;
      }
    } else if (value === null) {
      return "null";
    } else {
      return value;
    }
  };

  const handleExportCSV = () => {
    const csvRows = [];

    // Extract the table headers
    const headers = headCells.map((pair) => pair[0]) as any;
    csvRows.push(headers.join(","));

    // Extract the table data
    // eslint-disable-next-line
    sortedRows.map((row: any) => {
      let rowData = [] as any;
      // eslint-disable-next-line
      headers.map((key: any) => {
        const result = getMetricValueForCSV(row?.metric, key);
        if (result !== "null") {
          rowData.push(result);
        }
      });
      if (rowData?.length > 0) {
        csvRows.push(rowData.join(","));
      }
    });

    // Convert CSV rows to a single string
    const csvString = csvRows.join("\n");

    // Create a blob and download the file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "TargetVsActualPreviousMonth.csv");
  };

  return (
    <>
      <TableContainer
        className="RunRateTableWrap"
        component={Paper}
        sx={{ boxShadow: "none", maxHeight: 400, overflowY: "auto" }}
      >
        {sortedRows?.length > 0 ? (
          <>
            <Stack
              flexDirection={"row"}
              alignItems="center"
              gap={2}
              margin="4px 16px"
            >
              <Alert
                sx={{
                  color: "#ED6C02",
                  margin: "16px",
                  border: "1px solid #ED6C0280",
                  width: "100%",
                }}
                severity="warning"
                icon={<InfoOutlinedIcon fontSize="inherit" />}
              >
                PLEASE NOTE: Data is only relevant for the period of{" "}
                {dayjs(dates.startDate).format("DD-MM-YYYY")} -{" "}
                {dayjs(dates.endDate).format("DD-MM-YYYY")}
              </Alert>
              <IconButton color="secondary" size="large" data-tracker="275">
                <DownloadIcon fontSize="medium" onClick={handleExportCSV} />
              </IconButton>
            </Stack>
            <Table aria-label="collapsible table" size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headCells.map(([key, value], index) => (
                    <TableCell
                      key={index + "tabelCellpvmonth" + Math.random()}
                      align="left"
                    >
                      <TableSortLabel
                        active={orderBy === key}
                        direction={orderBy === key ? order : "asc"}
                        onClick={() => handleRequestSort(key)}
                      >
                        {value}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows?.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex + "pvmotnh" + Math.random()}>
                    {checkIfMetricsExist(
                      row?.metric,
                      lastMonthTargetVsActualData
                    ) ? (
                      <TableRow
                        sx={{
                          "&:not(:last-child) > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        <TableCell
                          key={1 + "tabelCellPvMonth" + Math.random()}
                          align="left"
                          sx={{ py: "3px", color: "#9c27b0" }}
                        >
                          <>
                            {getMetricValue(row?.metric, "Metrics")
                              ?.toString()
                              ?.replace(/_/g, " ")
                              ?.toUpperCase()}
                          </>
                        </TableCell>
                        <TableCell
                          key={2 + "tabelCellPvMonth" + Math.random()}
                          align="left"
                        >
                          {getMetricValue(row?.metric, "Actual")}
                        </TableCell>
                        <TableCell
                          key={3 + "tabelCellPvMonth" + Math.random()}
                          align="left"
                        >
                          {getMetricValue(row?.metric, "Target")}
                        </TableCell>
                        <TableCell
                          key={4 + "tabelCellPvMonth" + Math.random()}
                          align="left"
                        >
                          {getMetricValue(row?.metric, "Attainment")}
                        </TableCell>
                        <TableCell
                          key={5 + "tabelCellPvMonth" + Math.random()}
                          align="left"
                        >
                          {getMetricValue(row?.metric, "Gap")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      ""
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </>
        ) : (
          ""
        )}
      </TableContainer>
    </>
  );
};

export default TargetVSActualPreviousMonth;
